/*
 * The device drawer's Speed limit section: what this device is limited to and
 * why, a way to move it to another profile, and a way to give it limits of its
 * own, which makes a profile holding just this device. Silent on an edge whose
 * build has no device speed limits; the Device Speed Limits screen says why.
 *
 * ! file components/DeviceSpeedLimit.js
 */
import React, { useEffect, useState } from "react";
import useDeviceControls from "../hooks/useDeviceControls";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { LimitFields, limitsFromFields } from "./DeviceLimitsPanel";
import { useWanName } from "./WanNames";
import * as W from "./deviceWords";
import "./DeviceLimits.css";

export default function DeviceSpeedLimit({ nodeId, device, deviceName }) {
  const dc = useDeviceControls(nodeId);
  const wanName = useWanName();
  const [entry, setEntry] = useState(undefined);
  const [own, setOwn] = useState(null); // {down, up} while the own-limit form is open
  const mac = W.normalizeMac(device?.mac);
  const view = dc.view;
  const assigned = mac ? view?.devices?.[mac]?.profile || "" : "";

  useEffect(() => {
    setEntry(undefined);
    if (!nodeId || !mac || !view?.available) return undefined;
    let active = true;
    fetchWithTimeout(nodeApiUrlFor(nodeId)("/devices/controls/status"))
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (active) setEntry((body?.status?.devices || []).find((e) => e.mac === mac) || null);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [nodeId, mac, view, assigned]);

  if (!view?.available) return null;

  if (!mac) {
    return (
      <div className="drawer-section">
        <h3 className="drawer-section-title">Speed limit</h3>
        <p className="dl-drawer-note">{W.NO_MAC}</p>
      </div>
    );
  }

  const profiles = view.profiles || [];
  const joined = !assigned && !!view.new_devices;
  const profile = profiles.find((p) => p.id === (assigned || view.new_devices)) || null;
  const words = W.capped(profile) ? W.enforcementWords(entry) : null;

  const saveOwn = async (e) => {
    e.preventDefault();
    const saved = await dc.saveProfile({ name: W.ownProfileName(deviceName, profiles), ...limitsFromFields(own.down, own.up) });
    if (saved && (await dc.assign(mac, saved.id))) setOwn(null);
  };

  return (
    <div className="drawer-section">
      <h3 className="drawer-section-title">Speed limit</h3>
      <div className="dl-drawer-headline">{W.limitHeadline(profile)}</div>
      {(W.linesWords(profile, wanName) || W.bondingWords(profile) || W.classCountWords(profile)) && (
        <p className="dl-drawer-note">
          {[W.linesWords(profile, wanName), W.bondingWords(profile)].filter(Boolean).join(". ")}
          {(W.linesWords(profile, wanName) || W.bondingWords(profile)) && "."} {W.classCountWords(profile)}
        </p>
      )}
      <p className="dl-drawer-note">{W.limitSource(profile, joined)}</p>
      {words && entry !== undefined && (
        <p className={words.ok ? "dl-drawer-ok" : "dl-drawer-bad"}>{words.label}{words.detail ? `. ${words.detail}` : ""}</p>
      )}
      {dc.error && <p className="dl-drawer-bad" role="alert">{dc.error}</p>}

      {own ? (
        <form className="dl-drawer-form" onSubmit={saveOwn}>
          <LimitFields dark down={own.down} up={own.up} min={view.min_cap_mbit} max={view.max_cap_mbit}
            onChange={(k, v) => setOwn({ ...own, [k]: v })} />
          <div className="dl-actions">
            <button type="submit" className="dl-btn primary" disabled={dc.busy}>Save</button>
            <button type="button" className="dl-btn dark" onClick={() => setOwn(null)} disabled={dc.busy}>Cancel</button>
          </div>
        </form>
      ) : (
        <div className="dl-drawer-controls">
          <select className="dl-input dark" value={assigned} disabled={dc.busy}
            aria-label="Profile for this device" onChange={(e) => dc.assign(mac, e.target.value)}>
            <option value="">{view.new_devices ? "No profile of its own" : "No profile (router's settings)"}</option>
            {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}: {W.limitHeadline(p).toLowerCase()}</option>)}
          </select>
          <button type="button" className="dl-btn dark" onClick={() => setOwn({ down: "", up: "" })}>Give it its own limit</button>
        </div>
      )}
    </div>
  );
}
